import { useEffect, useMemo, type MutableRefObject, type Dispatch, type SetStateAction } from "react";
import { getFacebookConversationsWithMeta, checkConversationFolderStatus } from "@api/services/facebook";
import { getZaloConversationsWithMeta } from "@api/services/zalo";
import { CHANNELS } from "@common";
import type { Conversation } from "@interfaces/chat.interface";
import { useChatStore } from "@stores/chatStore";
import { ConversationTabHelpers, type ConversationTabType } from "@constants/conversationTabs";

type Page = { id: string; name: string; messageSyncStatus?: string };
type OA = { id: string; name: string };

type Params = {
  selectedTagIds: string[];
  selectedFanpageName?: string;
  selectedChannel: string;
  selectedPageId?: string;
  facebookPages: Page[];
  zaloOAs: OA[];
  debouncedSearchValue: string;
  activeTab: ConversationTabType;
  setConversationsWithSearch: (conversations: Conversation[], searchTerm: string) => void;
  setIsLoadingConversations: (value: boolean) => void;
  setConversationFolderStatusMap: Dispatch<
    SetStateAction<{ items: Array<{ folder?: string; externalId: string }> } | null>
  >;
  updateConversationFolderStatus: (folderStatusMap: Record<string, string>) => void;
  selectConversation: (conversationId: string | undefined) => void;
  isResettingPageRef: MutableRefObject<boolean>;
  isFetchingFromFolderStatusChangeRef: MutableRefObject<boolean>;
  searchDebounceRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  updatePagination: (pageId: string, pagination: { offset: number; total: number; limit: number; hasMore: boolean }) => void;
};

export const useConversationFetch = ({
  selectedTagIds,
  selectedFanpageName,
  selectedChannel,
  selectedPageId,
  facebookPages,
  zaloOAs,
  debouncedSearchValue,
  activeTab,
  setConversationsWithSearch,
  setIsLoadingConversations,
  setConversationFolderStatusMap,
  updateConversationFolderStatus,
  selectConversation,
  isResettingPageRef,
  isFetchingFromFolderStatusChangeRef,
  searchDebounceRef,
  updatePagination,
}: Params) => {
  const selectedTagIdsKey = useMemo(
    () => selectedTagIds.join(","),
    [selectedTagIds]
  );

  useEffect(() => {
    if (!selectedPageId) {
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    if (selectedChannel === CHANNELS.ZALO) {
      if (zaloOAs.length === 0) {
        return;
      }

      const selectedOA = zaloOAs.find((oa) => oa.id === selectedPageId);
      if (!selectedOA) {
        return;
      }

      const fetchZaloConversations = async () => {
        try {
          isFetchingFromFolderStatusChangeRef.current = true;
          setIsLoadingConversations(true);

          const result = await getZaloConversationsWithMeta({
            oaId: selectedOA.id,
            offset: 0,
            limit: 50,
            signal,
          });

          const conversationsWithFanpage = result.conversations.map((conv) => ({
            ...conv,
            fanpageName: selectedFanpageName,
          }));

          const { conversations: currentConversations } = useChatStore.getState();
          const conversationsFromOtherFanpages = currentConversations.filter(
            (conv) =>
              !(
                conv.fanpageName === selectedFanpageName &&
                conv.channel === CHANNELS.ZALO
              )
          );

          if (signal.aborted || isResettingPageRef.current) {
            return;
          }

          const allConversations = [
            ...conversationsWithFanpage,
            ...conversationsFromOtherFanpages,
          ];

          const expectedSearchTerm = debouncedSearchValue.trim() || "";
          const currentState = useChatStore.getState();

          if (
            currentState.selectedChannel !== CHANNELS.ZALO ||
            currentState.selectedFanpageName !== selectedFanpageName
          ) {
            setIsLoadingConversations(false);
            isFetchingFromFolderStatusChangeRef.current = false;
            return;
          }

          const getFilteredConversations = (conversations: Conversation[]) => {
            let filtered = conversations.filter((conv) => conv.channel === CHANNELS.ZALO);

            if (selectedFanpageName) {
              filtered = filtered.filter((conv) => conv.fanpageName === selectedFanpageName);
            }

            if (expectedSearchTerm) {
              filtered = filtered.filter((conv) =>
                conv.name.toLowerCase().includes(expectedSearchTerm.toLowerCase())
              );
            }

            return filtered.sort((a, b) => {
              if (b.isPinned !== a.isPinned) {
                return Number(b.isPinned) - Number(a.isPinned);
              }
              const aTime = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
              const bTime = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
              return bTime - aTime;
            });
          };

          if (currentState.selectedChannel !== CHANNELS.ZALO) {
            const filtered = getFilteredConversations(allConversations);
            useChatStore.setState(() => ({
              conversations: allConversations,
              searchTerm: expectedSearchTerm,
              selectedChannel: CHANNELS.ZALO,
              filteredConversations: filtered,
            }));
          } else {
            setConversationsWithSearch(allConversations, expectedSearchTerm);
          }

          updatePagination(selectedOA.id, {
            offset: result.offset,
            total: result.total,
            limit: result.limit,
            hasMore: result.offset + result.conversations.length < result.total,
          });

          setIsLoadingConversations(false);
          isFetchingFromFolderStatusChangeRef.current = false;
        } catch (error) {
          if (!signal.aborted) {
            console.error("Error fetching Zalo conversations:", error);
            setIsLoadingConversations(false);
            isFetchingFromFolderStatusChangeRef.current = false;
          }
        }
      };

      fetchZaloConversations();

      return () => {
        controller.abort();
      };
    }

    if (selectedChannel !== CHANNELS.FACEBOOK) {
      return;
    }

    if (!selectedPageId) {
      return;
    }

    const selectedPage = facebookPages.find(
      (page) => page.id === selectedPageId
    );

    if (!selectedPage) {
      return;
    }

    const fetchConversationsWithTags = async () => {
      let hasFolderStatusCheck = false;
      try {
        isFetchingFromFolderStatusChangeRef.current = true;
        setIsLoadingConversations(true);

        let folder: string | undefined;
        let excludeFolders: string[] | undefined;

        if (!ConversationTabHelpers.requiresFolderStatus(activeTab)) {
          excludeFolders = [];
        } else {
          folder = ConversationTabHelpers.getFolderForTab(activeTab);
        }

        const result = await getFacebookConversationsWithMeta({
          pageId: selectedPage.id,
          offset: 0,
          limit: 50,
          tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
          search: debouncedSearchValue.trim() || undefined,
          folder,
          excludeFolders,
          signal,
        });

        const conversationsWithFanpage = result.conversations.map((conv) => ({
          ...conv,
          fanpageName: selectedFanpageName,
        }));

        const { conversations: currentConversations } = useChatStore.getState();
        const conversationsFromOtherFanpages = currentConversations.filter(
          (conv) =>
            !(
              conv.fanpageName === selectedFanpageName &&
              conv.channel === CHANNELS.FACEBOOK
            )
        );

        if (signal.aborted || isResettingPageRef.current) {
          return;
        }

        const allConversations = [
          ...conversationsWithFanpage,
          ...conversationsFromOtherFanpages,
        ];

        const expectedSearchTerm = debouncedSearchValue.trim() || "";
        const currentState = useChatStore.getState();
        if (
          currentState.selectedChannel !== CHANNELS.FACEBOOK ||
          currentState.selectedFanpageName !== selectedFanpageName
        ) {
          return;
        }

        const getFilteredConversations = (conversations: Conversation[]) => {
          let filtered = conversations.filter((conv) => conv.channel === CHANNELS.FACEBOOK);

          if (selectedFanpageName) {
            filtered = filtered.filter((conv) => conv.fanpageName === selectedFanpageName);
          }

          if (expectedSearchTerm) {
            filtered = filtered.filter((conv) =>
              conv.name.toLowerCase().includes(expectedSearchTerm.toLowerCase())
            );
          }

          return filtered.sort((a, b) => {
            if (b.isPinned !== a.isPinned) {
              return Number(b.isPinned) - Number(a.isPinned);
            }
            const aTime = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
            const bTime = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
            return bTime - aTime;
          });
        };

        if (currentState.selectedChannel !== CHANNELS.FACEBOOK) {
          const filtered = getFilteredConversations(allConversations);
          useChatStore.setState(() => ({
            conversations: allConversations,
            searchTerm: expectedSearchTerm,
            selectedChannel: CHANNELS.FACEBOOK,
            filteredConversations: filtered,
          }));
        } else {
          setConversationsWithSearch(allConversations, expectedSearchTerm);
        }

        updatePagination(selectedPage.id, {
          offset: result.offset,
          total: result.total,
          limit: result.limit,
          hasMore: result.offset + result.conversations.length < result.total,
        });

        if (conversationsWithFanpage.length > 0 && !signal.aborted && !isResettingPageRef.current) {
          const externalIds = conversationsWithFanpage
            .filter((conv) => conv.channel === CHANNELS.FACEBOOK)
            .map((conv) => conv.lastMessage?.conversationId)
            .filter((id): id is string => Boolean(id));

          if (externalIds.length > 0) {
            hasFolderStatusCheck = true;
            checkConversationFolderStatus({
              pageId: selectedPage.id,
              externalIds,
              folders: ["DONE"],
            })
              .then((folderStatusResponse) => {
                if (signal.aborted || isResettingPageRef.current) {
                  isFetchingFromFolderStatusChangeRef.current = false;
                  return;
                }

                if (folderStatusResponse.code === 0 && folderStatusResponse.data) {
                  const newItems = (folderStatusResponse.data.items || []) as Array<{
                    externalId: string;
                    folder?: string;
                  }>;
                  setConversationFolderStatusMap((prev) => {
                    if (!prev) {
                      return { items: newItems };
                    }
                    const existingItemsMap = new Map(
                      prev.items.map((item: { externalId: string; folder?: string }) => [
                        item.externalId,
                        item,
                      ])
                    );
                    newItems.forEach((item: { externalId: string; folder?: string }) => {
                      existingItemsMap.set(item.externalId, item);
                    });
                    return {
                      items: Array.from(existingItemsMap.values()),
                    };
                  });
                  const folderStatusMapToStore: Record<string, string> = {};
                  newItems.forEach((item) => {
                    if (item.folder) {
                      folderStatusMapToStore[item.externalId] = item.folder;
                    }
                  });
                  updateConversationFolderStatus(folderStatusMapToStore);
                }
                isFetchingFromFolderStatusChangeRef.current = false;
              })
              .catch((error) => {
                if (!signal.aborted) {
                  console.error("Error checking folder status:", error);
                }
                isFetchingFromFolderStatusChangeRef.current = false;
              });
          }
        }

        const messageSyncStatus = selectedPage?.messageSyncStatus;
        const isInProgress = messageSyncStatus === "IN_PROGRESS";

        if (messageSyncStatus === "SYNCED") {
          // Auto-select handled elsewhere
        } else if (isInProgress) {
          const { selectedConversationId: currentConversationId } =
            useChatStore.getState();
          if (currentConversationId) {
            selectConversation(undefined);
          }
        }
      } catch (error) {
        if (!signal.aborted) {
          console.error("Error fetching conversations with tags:", error);
        }
      } finally {
        if (!signal.aborted) {
          setIsLoadingConversations(false);
          if (!hasFolderStatusCheck) {
            isFetchingFromFolderStatusChangeRef.current = false;
          }
        }
      }
    };

    fetchConversationsWithTags();

    return () => {
      controller.abort();
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [
    selectedTagIdsKey,
    selectedTagIds,
    selectedFanpageName,
    selectedChannel,
    selectedPageId,
    facebookPages,
    zaloOAs,
    debouncedSearchValue,
    activeTab,
    setConversationsWithSearch,
    setIsLoadingConversations,
    setConversationFolderStatusMap,
    updateConversationFolderStatus,
    selectConversation,
    isResettingPageRef,
    isFetchingFromFolderStatusChangeRef,
    searchDebounceRef,
    updatePagination,
  ]);
};
